<template>
    <div class="container-fluid">
        <div class="card">
            <div class="card-header d-flex align-items-center justify-content-between">
                <div class="d-flex align-items-center">
                    <i class="fas fa-file-alt me-2"></i>
                    <h4 class="mb-0">{{ isNew ? 'New Form Template' : 'Edit Form Template' }}</h4>
                </div>
                <button class="btn btn-sm btn-outline-secondary" @click="goBack">
                    <i class="fas fa-arrow-left me-1"></i> Back
                </button>
            </div>
            <div class="card-body">
                <div v-if="loading" class="text-center py-5">
                    <div class="spinner-border" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                </div>
                
                <form v-else @submit.prevent="save">
                    <div class="row">
                        <div class="col-md-6 mb-3">
                            <label class="form-label">Title <span class="text-danger">*</span></label>
                            <input 
                                type="text" 
                                class="form-control" 
                                v-model="form.Title"
                                required
                                placeholder="Enter form title"
                            />
                        </div>
                        <div class="col-md-6 mb-3">
                            <label class="form-label">Category</label>
                            <input 
                                type="text" 
                                class="form-control" 
                                v-model="form.Category"
                                placeholder="Enter category"
                            />
                        </div>
                    </div>

                    <!-- 
                    <div class="row">
                        <div class="col-md-6 mb-3">
                            <label class="form-label">Reference Type <span class="text-danger">*</span></label>
                            <select class="form-select" v-model="form.RefType" required>
                                <option value="">Select type...</option>
                                <option value="Request">Request</option>
                                <option value="Contact">Contact</option>
                                <option value="Technician">Technician</option>
                                <option value="General">General</option>
                            </select>
                        </div>
                        <div class="col-md-6 mb-3">
                            <label class="form-label">Reference ID</label>
                            <input 
                                type="text" 
                                class="form-control" 
                                v-model="form.RefId"
                                placeholder="Optional reference ID"
                            />
                        </div>
                    </div>
                    -->

                    <div class="mb-3">
                        <label class="form-label">Description</label>
                        <textarea 
                            class="form-control" 
                            v-model="form.Description"
                            rows="3"
                            placeholder="Enter description"
                        ></textarea>
                    </div>

                    <!-- 
                    <div class="mb-3">
                        <label class="form-label">Form Schema (JSON)</label>
                        <textarea 
                            class="form-control font-monospace" 
                            v-model="form.FormSchema"
                            rows="10"
                            placeholder='{"fields": []}'
                        ></textarea>
                        <small class="text-muted">Define the form structure in JSON format</small>
                    </div>
                    -->

                    <div class="row">
                        <div class="col-md-6 mb-3">
                            <label class="form-label">Version</label>
                            <input 
                                type="number" 
                                class="form-control" 
                                v-model="form.Version"
                                min="1"
                            />
                        </div>
                        <div class="col-md-6 mb-3">
                            <div class="form-check mt-4">
                                <input 
                                    class="form-check-input" 
                                    type="checkbox" 
                                    v-model="form.IsActive"
                                    id="isActiveCheck"
                                />
                                <label class="form-check-label" for="isActiveCheck">
                                    Active
                                </label>
                            </div>
                        </div>
                    </div>

                    <!-- Form Builder -->
                    <div class="mb-4">
                        <label class="form-label">Form Structure</label>
                        <FormBuilder 
                            :initial-structure="formStructure"
                            @update="handleFormStructureUpdate"
                        />
                    </div>

                    <div class="d-flex gap-2">
                        <button type="submit" class="btn btn-primary" :disabled="saving">
                            <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
                            <i v-else class="fas fa-save me-1"></i>
                            Save
                        </button>
                        <button type="button" class="btn btn-secondary" @click="goBack">
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import Api, { FormTemplateModel } from "@/services/ApiService";
import Emitter from "@/services/Emitter";
import FormBuilder from "@/components/FormBuilder/FormBuilder.vue";
import type { FormStructure } from "@/components/types/form-builder";
import { createEmptyFormStructure } from "@/components/utils/form-helpers";

const route = useRoute();
const router = useRouter();

const loading = ref(false);
const saving = ref(false);

const form = ref<FormTemplateModel>({
    Title: '',
    Description: '',
    RefType: 'General',
    RefId: '',
    FormSchema: '{}',
    IsActive: true,
    Category: '',
    Version: 1
});

// Parse FormSchema JSON to FormStructure for the builder
const formStructure = computed<FormStructure>(() => {
    try {
        const parsed = JSON.parse(form.value.FormSchema || '{}');
        if (parsed.version && Array.isArray(parsed.containers)) {
            return parsed;
        }
    } catch {
        // Invalid JSON, return empty structure
    }
    return createEmptyFormStructure();
});

// Handle updates from FormBuilder
const handleFormStructureUpdate = (structure: FormStructure) => {
    form.value.FormSchema = JSON.stringify(structure);
};

const isNew = computed(() => {
    return route.params.id === 'new' || !route.params.id;
});

const loadForm = async () => {
    if (isNew.value) return;
    
    loading.value = true;
    try {
        const id = route.params.id as string;
        const response = await Api.getFormTemplate(id);
        if (response.Data) {
            form.value = response.Data;
        }
    } catch (error) {
        console.error('Failed to load form template:', error);
    } finally {
        loading.value = false;
    }
};

const save = async () => {
    saving.value = true;
    try {
        await Api.saveFormTemplate(form.value);
        Emitter.emit('datatable-refresh', 'form-template-list');
        router.push('/forms');
    } catch (error) {
        console.error('Failed to save form template:', error);
    } finally {
        saving.value = false;
    }
};

const goBack = () => {
    router.push('/forms');
};

onMounted(() => {
    loadForm();
});
</script>

<style scoped>
.card-header {
    background-color: #f8f9fa;
    border-bottom: 1px solid #dee2e6;
}

.font-monospace {
    font-family: 'Courier New', Courier, monospace;
    font-size: 0.875rem;
}
</style>
